import {
  formValue,
  useVaultSubmit,
  VaultFormComposable,
} from "@snap-mobile/vault";
import { useEffect, useState } from "react";
import { print } from "graphql/language/printer";
import OrganizationForm from "./pages/organization-form";
import OfficerForm from "./pages/officer-form";
import BeneficialOwnersForm from "./pages/beneficial-owners";
import { BeneficialOwnerData, Page } from "./types";
import CompletePage from "./pages/complete";
import {
  BUSINESS_STRUCTURES,
  BUSINESS_STRUCTURES_THAT_REQUIRE_OWNERS,
  BUSINESS_TYPES,
  DEFAULT_PERCENT_OWNERSHIP,
  STATES,
  TITLES,
} from "./const";
import { useQuery } from "@apollo/client";
import { ME } from "graphql/queries/user";
import { SnapLoader } from "shared-components/snap-loader";
import ErrorPage from "./pages/error";
import { VAULT_CREATE_KYB_KYC_SUBMIT } from "graphql/mutations/vault";
import useUser from "hooks/use-user";
import { useNavigate } from "react-router-dom";

function ApplicationFormV2() {
  const submit = useVaultSubmit();
  const [currentPage, setCurrentPage] = useState<Page | undefined>(
    "organization"
  );
  const [kybType, setKybType] = useState<string>(BUSINESS_TYPES[0].value || "");
  const [kybStructure, setKybStructure] = useState<string>(
    BUSINESS_STRUCTURES[BUSINESS_TYPES[0].value || ""][0].value || ""
  );
  const [kybState, setKybState] = useState<string>(STATES[0].value || "");
  const [kybStateOfIncorporation, setKybStateOfIncorporation] =
    useState<string>(STATES[0].value || "");
  const [officerTitle, setOfficerTitle] = useState<string>(
    TITLES[0].value || ""
  );
  const [officerState, setOfficerState] = useState<string>(
    STATES[0].value || ""
  );
  const [officerIsOwner, setOfficerIsOwner] = useState(false);
  const [officerPercentOwnership, setOfficerPercentOwnership] = useState(
    DEFAULT_PERCENT_OWNERSHIP
  );
  const [beneficialOwnersData, setBeneficialOwnersData] = useState<
    BeneficialOwnerData[]
  >([]);
  const { data: ssoUserData } = useQuery(ME);
  const [error, setError] = useState<unknown>();
  const sessionUser = useUser();
  const navigate = useNavigate();

  const askForBeneficialOwners =
    kybStructure !== undefined &&
    BUSINESS_STRUCTURES_THAT_REQUIRE_OWNERS.includes(kybStructure);

  useEffect(() => {
    if (!sessionUser) {
      return;
    }
    if ((sessionUser?._allRoles || []).length > 0) {
      navigate("/dashboard");
    }
  }, [navigate, sessionUser]);

  const handleSubmit = async () => {
    setCurrentPage(undefined);

    try {
      const submitRes = await submit({
        query: print(VAULT_CREATE_KYB_KYC_SUBMIT),
        variables: {
          gateway: "unit",
          rawOrgData: {
            address: {
              city: formValue("kybCity"),
              postalCode: formValue("kybZip"),
              region: kybState,
              street: formValue("kybStreet"),
              unit: formValue("kybUnit"),
            },
            businessVertical: null,
            customerFacingName: formValue("kybCustomerFacingName"),
            description: null,
            email: formValue("kybEmail"),
            legalName: formValue("kybLegalName"),
            phoneNumber: formValue("kybPhone"),
            stateOfIncorporation: kybStateOfIncorporation,
            structure: kybStructure,
            taxId: formValue("kybEin"),
            type: kybType,
            url: formValue("kybYear"),
            yearOfIncorporation: formValue("kybYearOfIncorporation"),
          },
          rawRepresentativeData: {
            userId: ssoUserData.me.id,
            kycData: {
              address: {
                city: formValue("officerKycCity"),
                postalCode: formValue("officerKycZip"),
                region: officerState,
                street: formValue("officerKycStreet"),
                unit: formValue("officerKycUnit"),
              },
              director: false,
              dob: {
                day: formValue("officerKycDobDay"),
                month: formValue("officerKycDobMonth"),
                year: formValue("officerKycDobYear"),
              },
              email: formValue("officerKycEmail"),
              executive: true,
              firstName: formValue("officerKycFirstName"),
              lastName: formValue("officerKycLastName"),
              owner: officerIsOwner,
              percentOwnership: officerIsOwner ? officerPercentOwnership : null,
              phoneNumber: formValue("officerKycPhone"),
              representative: true,
              ssn: formValue("officerKycSsn"),
              title: officerTitle,
            },
          },
          rawBeneficialOwners: beneficialOwnersData.map((bo) => ({
            address: {
              city: formValue(`beneficialOwner${bo.id}KycCity`),
              postalCode: formValue(`beneficialOwner${bo.id}KycZip`),
              region: bo.state,
              street: formValue(`beneficialOwner${bo.id}KycStreet`),
              unit: formValue(`beneficialOwner${bo.id}KycUnit`),
            },
            director: false,
            dob: {
              day: formValue(`beneficialOwner${bo.id}KycDobDay`),
              month: formValue(`beneficialOwner${bo.id}KycDobMonth`),
              year: formValue(`beneficialOwner${bo.id}KycDobYear`),
            },
            email: formValue(`beneficialOwner${bo.id}KycEmail`),
            executive: false,
            firstName: formValue(`beneficialOwner${bo.id}KycFirstName`),
            lastName: formValue(`beneficialOwner${bo.id}KycLastName`),
            owner: true,
            percentOwnership: bo.percentOwnership,
            phoneNumber: formValue(`beneficialOwner${bo.id}KycPhone`),
            representative: false,
            ssn: formValue(`beneficialOwner${bo.id}KycSsn`),
            title: null,
          })),
        },
      });
      if (
        (submitRes as { vaultCreateKybKycSubmit: { success: boolean } })
          .vaultCreateKybKycSubmit.success
      ) {
        setCurrentPage("complete");
      } else {
        setError(`Submit encountered error ${JSON.stringify(submitRes)}`);
      }
    } catch (error) {
      setError(error);
      console.log("Submit error", false, error);
      setCurrentPage("error");
    }
  };
  return (
    <>
      {currentPage === undefined && <SnapLoader />}
      <div
        style={{ visibility: currentPage === undefined ? "hidden" : "visible" }}
      >
        <OrganizationForm
          visible={currentPage === "organization"}
          setCurrentPage={setCurrentPage}
          onKybTypeChange={setKybType}
          onKybStructureChange={setKybStructure}
          onKybStateChange={setKybState}
          onKybStateOfIncorporationChange={setKybStateOfIncorporation}
        />
        <OfficerForm
          visible={currentPage === "officer"}
          setCurrentPage={setCurrentPage}
          askForBeneficialOwners={askForBeneficialOwners}
          onOfficerStateSelect={setOfficerState}
          onOfficerTitleSelect={setOfficerTitle}
          onSubmit={handleSubmit}
          onOfficerIsOwnerChange={setOfficerIsOwner}
          onOfficerPercentOwnershipChange={setOfficerPercentOwnership}
        />
        {askForBeneficialOwners && (
          <BeneficialOwnersForm
            visible={currentPage === "beneficial_owners"}
            setCurrentPage={setCurrentPage}
            onBeneficialOwnersValues={setBeneficialOwnersData}
            onSubmit={handleSubmit}
          />
        )}
        <CompletePage visible={currentPage === "complete"} />
        <ErrorPage visible={currentPage === "error"} error={error} />
      </div>
    </>
  );
}

export default function Wrapper() {
  return (
    <VaultFormComposable>
      <ApplicationFormV2 />
    </VaultFormComposable>
  );
}
